import { AxiosError } from 'axios';
import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from '../../../../../../components/Modal';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../../../components/Notification';
import { NotificationTypes } from '../../../../../../components/Notification/notificationEnums';
import HttpStatus from '../../../../../../enums/httpStatus';
import { apiKeysReq, showApiKeyReq } from '../../../../../../services/requests';
import { ApiKey } from '../../../../../../types';

type Props = {
  showModal: boolean;
  closeModal: () => void;
  apiKeySelected: ApiKey;
};

const CopyApiKeyModal = ({ showModal, closeModal, apiKeySelected }: Props) => {
  const { t } = useTranslation();

  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleCopyApiKey = async () => {
    setIsLoading(true);

    try {
      const response = await showApiKeyReq(apiKeySelected.code, password);

      if (response.status !== HttpStatus.OK) {
        throw response;
      }

      const apiKeyValue = response.data?.data?.api_key;

      if (!apiKeyValue) {
        frontendNotification({
          message: t('copyFailed'),
          type: NotificationTypes.ERROR,
        });
        setIsLoading(false);
        return;
      }

      await navigator.clipboard.writeText(apiKeyValue);

      frontendNotification({
        message: t('copiedSuccessfully'),
        type: NotificationTypes.SUCCESS,
      });

      closeModal();
      setPassword('');
    } catch (err) {
      if (!apiKeysReq.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    }

    setIsLoading(false);
  };

  return (
    <Modal
      title={t('copyApiKey')}
      showModal={showModal}
      closeModal={closeModal}
      cancelButton={closeModal}
      action={handleCopyApiKey}
      actionNameBtn={t('copy')}
      disableActionBtn={!password}
      isLoading={isLoading}
      bodyStyle={{ overflow: 'scroll' }}
    >
      <div className="row">
        <div className="inputWithoutLabel">
          <p>{t('apiKeyWillBeCopied')}</p>
          <b>{apiKeySelected.name}</b>
        </div>
      </div>

      <div className="row">
        <div className="inputWithLabel">
          <p className="label">{t('password') as string}</p>
          <input
            className="input"
            type="password"
            value={password}
            onChange={
              (event: ChangeEvent<HTMLInputElement>) => setPassword(event.currentTarget.value)
            }
          />
        </div>
      </div>
    </Modal>
  );
};

export default CopyApiKeyModal;